/**
 * Shared live-analysis loop. Extracted from the previously-duplicated RAF
 * bodies in `TestVideo.svelte` and `Record.svelte` so both routes get the
 * same stickman + mini-quadra + ball + court + scene-change + rally pipeline
 * against either a file-backed `<video>` or the live camera `<video>`.
 *
 * Design notes
 * ------------
 * - The class owns all mutable per-frame state (captured poses, seen
 *   contacts, ball tracker, court calibrator, scene-change detector, throttle
 *   timestamps, rally state) and exposes a single callback `onFrame(state)`
 *   plus reactive getters so Svelte components can either subscribe or just
 *   re-read snapshots every tick.
 * - `start()`, `pause()`, `stop()` and `reset()` follow a MediaRecorder-ish
 *   lifecycle. `stop()` is final: call `dispose()` before dropping.
 * - Camera-cut handling: `SceneChangeDetector` runs first; on a cut the
 *   court-calibration EMA and ball Kalman are reset and the frame is SKIPPED
 *   entirely (no pose, no calibration, no contact accumulation) — this is
 *   what fixes the "overlays blended across angles" bug.
 * - Rally segmentation: `detectRallies` runs alongside the contact re-analysis
 *   every ~1 s; when a previously-open rally closes, `onRallyEnd` fires with
 *   the closed `Rally`. Routes use that to show the "Ponto encerrado" toast.
 */

import type { ContactEvent, PoseFrame } from '@tbc/core';
import { pose, score } from '@tbc/core';
import { pixelToCourt, COURT_M, type Matrix3x3 } from '@tbc/core/court';
import type { Rally } from '@tbc/core/score';
import { analyzeSession } from '../swingAnalysis.js';
import { PoseRunnerProxy } from '../poseRunnerProxy.js';
import { clearCanvas, drawStickman } from '../overlay.js';
import { createBallLiveState, type BallLiveController } from '../tracking/ballLiveState.js';
import { createCourtLiveState, type CourtLiveStateHandle } from '../tracking/courtLiveState.js';
import { SceneChangeDetector } from '../tracking/sceneChange.js';
import {
  COURT,
  deterministicImpact,
} from '../court/geometry.js';
import type {
  CourtImpact,
  CourtTimelineSample,
  LivePlayers,
  LiveBall,
} from '../court/types.js';
import type { OverlayFlags, UserSettings } from '../settings/settings.svelte.js';
import { DEFAULT_SETTINGS } from '../settings/settings.svelte.js';

export type LoopStatus = 'idle' | 'running' | 'paused' | 'stopped';

export interface LiveAnalysisState {
  /** Pose frames kept for downstream analysis (pose of the first detected player). */
  readonly capturedLength: number;
  readonly livePlayers: LivePlayers;
  readonly liveBall: LiveBall | null;
  readonly liveImpacts: readonly CourtImpact[];
  readonly lastEvent: CourtImpact | null;
  readonly calibCorners: readonly [number, number][] | null;
  readonly calibState: 'pending' | 'converged' | 'failed';
  /** Monotonically-increasing cut count, useful for UI hints/debug badges. */
  readonly cutCount: number;
  readonly status: LoopStatus;
}

export interface LiveAnalysisOptions {
  /** Optional settings snapshot. Defaults are used when omitted. */
  readonly settings?: Partial<UserSettings>;
  /** Called every time a frame was processed (including skipped-by-cut). */
  readonly onFrame?: (state: LiveAnalysisState) => void;
  /** Fires when a rally ends (for the "Ponto encerrado" toast). */
  readonly onRallyEnd?: (rally: Rally) => void;
  /** Optional error hook — default logs to console. */
  readonly onError?: (err: unknown) => void;
}

/** Shape consumed by LiveAnalysisLoop — keeps `<video>` and proxy pluggable for tests. */
export interface LiveAnalysisIO {
  readonly video: HTMLVideoElement;
  readonly canvas: HTMLCanvasElement | null;
  readonly proxy: PoseRunnerProxy;
}

const LIVE_REANALYZE_EVERY_N_FRAMES = 30; // ~1 s at 30 FPS
const MAX_CONSECUTIVE_CALIB_FAILURES = 5;

export class LiveAnalysisLoop {
  private readonly io: LiveAnalysisIO;
  private readonly opts: LiveAnalysisOptions;
  private settings: UserSettings;

  private readonly ballCtrl: BallLiveController;
  private readonly courtCtrl: CourtLiveStateHandle;
  private readonly sceneDet: SceneChangeDetector;

  private rafHandle: number | null = null;
  private inflight = false;
  private lastDetectMs = 0;

  private _status: LoopStatus = 'idle';
  private readonly captured: PoseFrame[] = [];
  private readonly timeline: CourtTimelineSample[] = [];
  private lastAnalyzedFrameCount = 0;
  private seenContactFrames = new Set<number>();
  private lastKnownContacts: ContactEvent[] = [];
  private openRallyStartIdx: number | null = null;
  private sessionSeed = Math.floor(Math.random() * 1_000_000);

  private livePlayers: LivePlayers = {};
  private liveBall: LiveBall | null = null;
  private liveImpacts: CourtImpact[] = [];
  private lastEvent: CourtImpact | null = null;
  private calibCorners: readonly [number, number][] | null = null;
  private calibState: 'pending' | 'converged' | 'failed' = 'pending';
  private framesSinceCalibrate = 0;
  private cutCount = 0;

  constructor(io: LiveAnalysisIO, opts: LiveAnalysisOptions = {}) {
    this.io = io;
    this.opts = opts;
    this.settings = { ...DEFAULT_SETTINGS, ...(opts.settings ?? {}) };
    this.ballCtrl = createBallLiveState();
    this.courtCtrl = createCourtLiveState();
    this.sceneDet = new SceneChangeDetector({
      threshold: this.settings.sceneChangeThreshold,
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  get status(): LoopStatus {
    return this._status;
  }

  get state(): LiveAnalysisState {
    return {
      capturedLength: this.captured.length,
      livePlayers: this.livePlayers,
      liveBall: this.liveBall,
      liveImpacts: this.liveImpacts,
      lastEvent: this.lastEvent,
      calibCorners: this.calibCorners,
      calibState: this.calibState,
      cutCount: this.cutCount,
      status: this._status,
    };
  }

  /** Full list of captured pose frames (reference, not a copy). */
  get capturedFrames(): readonly PoseFrame[] {
    return this.captured;
  }

  /**
   * Per-frame samples (player + ball court positions) captured during the
   * run. SwingDetail uses this to replay the mini-quadra in sync with the
   * recorded video blob.
   */
  get courtTimeline(): readonly CourtTimelineSample[] {
    return this.timeline;
  }

  /** Refresh the live settings. Next frame picks up the change. */
  applySettings(s: Partial<UserSettings>): void {
    this.settings = { ...this.settings, ...s };
  }

  start(): void {
    if (this._status === 'running') return;
    this._status = 'running';
    this.rafHandle = requestAnimationFrame(() => void this.tick());
  }

  pause(): void {
    if (this._status !== 'running') return;
    this._status = 'paused';
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
  }

  stop(): void {
    this._status = 'stopped';
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
  }

  /** Clear all per-session state. Must be called between runs. */
  reset(): void {
    this.captured.length = 0;
    this.timeline.length = 0;
    this.lastAnalyzedFrameCount = 0;
    this.seenContactFrames = new Set();
    this.lastKnownContacts = [];
    this.openRallyStartIdx = null;
    this.liveImpacts = [];
    this.lastEvent = null;
    this.livePlayers = {};
    this.liveBall = null;
    this.calibCorners = null;
    this.calibState = 'pending';
    this.framesSinceCalibrate = 0;
    this.cutCount = 0;
    this.sessionSeed = Math.floor(Math.random() * 1_000_000);
    this.ballCtrl.reset();
    this.courtCtrl.reset();
    this.sceneDet.reset();
    this.lastDetectMs = 0;
  }

  dispose(): void {
    this.stop();
    this.ballCtrl.dispose();
  }

  /**
   * Force a synchronous ingestion of one frame. Exposed for unit tests that
   * can't rely on `requestAnimationFrame`.
   */
  async processFrameOnce(): Promise<void> {
    await this.frame();
  }

  /** Compute a detection interval (ms) from the configured detection FPS. */
  private detectIntervalMs(): number {
    const fps = Math.max(1, this.settings.detectionFps);
    return Math.floor(1000 / fps);
  }

  /**
   * Simulate the full frame pipeline for tests. Drives the same code path as
   * the RAF tick but without scheduling a follow-up frame.
   */
  async __processForTest(): Promise<LiveAnalysisState> {
    await this.frame();
    return this.state;
  }

  // ---------------------------------------------------------------------------
  // Main tick
  // ---------------------------------------------------------------------------

  private async tick(): Promise<void> {
    if (this._status !== 'running') return;
    try {
      await this.frame();
    } catch (err) {
      (this.opts.onError ?? console.warn.bind(console))(err);
    }
    if (this._status === 'running') {
      this.rafHandle = requestAnimationFrame(() => void this.tick());
    }
  }

  private async frame(): Promise<void> {
    const { video, canvas, proxy } = this.io;
    if (!video || !proxy) return;
    if (this.inflight) return;
    if (video.readyState < 2) return;

    const now = performance.now();
    if (now - this.lastDetectMs < this.detectIntervalMs()) return;
    this.lastDetectMs = now;
    this.inflight = true;

    try {
      const bitmap = await safeCreateImageBitmap(video);
      if (!bitmap) return;
      const ts = video.currentTime * 1000 || now;

      // 1. Scene-change detection runs before everything else: on a cut we
      //    reset the calibrators and skip the rest of the frame.
      const cutResult = this.sceneDet.ingest(bitmap);
      if (cutResult.isCut) {
        this.cutCount += 1;
        this.courtCtrl.reset();
        this.ballCtrl.reset();
        this.calibCorners = null;
        this.calibState = 'pending';
        this.framesSinceCalibrate = 0;
        this.liveBall = null;
        this.livePlayers = {};
        this.emitFrame();
        closeBitmap(bitmap);
        return;
      }
      // Brightness-drop gate: crowd / close-up frames won't produce useful
      // pose — skip them when `pauseOnCut` is active.
      if (cutResult.isDarkDrop && this.settings.pauseOnCut) {
        this.emitFrame();
        closeBitmap(bitmap);
        return;
      }

      // 2. Court re-calibration every N frames.
      const calibIntervalFrames = Math.max(
        1,
        this.settings.recalibrateEverySec * this.settings.detectionFps,
      );
      this.framesSinceCalibrate += 1;
      if (
        this.courtCtrl.state.homography === null ||
        this.framesSinceCalibrate >= calibIntervalFrames
      ) {
        this.framesSinceCalibrate = 0;
        this.courtCtrl.tryCalibrate(bitmap);
        if (this.courtCtrl.state.homography !== null) {
          this.calibState = 'converged';
        } else if (
          this.courtCtrl.state.consecutiveFailures >= MAX_CONSECUTIVE_CALIB_FAILURES
        ) {
          this.calibState = 'failed';
        }
      }
      this.calibCorners = this.courtCtrl.state.corners;

      // 3. Pose detection + drawing.
      const poses = await proxy.detectMulti(bitmap, ts);
      this.drawOverlay(canvas, video, poses);

      const primary = poses[0];
      if (primary) this.captured.push(primary);

      // 4. Player + ball court positions for the mini quadra.
      const p1 = primary ? this.poseToCourtXY(primary) : null;
      const p2 = poses[1] ? this.poseToCourtXY(poses[1]!) : null;
      this.livePlayers = { p1, p2 };

      const ballNorm = this.ballCtrl.feed(video, ts);
      let ballCourt: { x: number; y: number } | null = null;
      if (ballNorm) {
        ballCourt = this.ballToCourtXY(ballNorm);
        const trail = this.ballCtrl.state.trail.map((p) => this.ballToCourtXY(p));
        this.liveBall = {
          x: ballCourt.x,
          y: ballCourt.y,
          confidence: ballNorm.confidence,
          trail,
        };
      } else {
        this.liveBall = null;
      }

      // Append a frame sample to the replay timeline. `ts` matches video
      // currentTime in ms so downstream scrubbers can match 1:1 with the
      // saved blob clock.
      this.timeline.push({
        tMs: ts,
        p1,
        p2,
        ball: ballCourt,
        ballConf: ballNorm?.confidence ?? 0,
      });

      // 5. Periodic re-analysis for live impacts + rally segmentation.
      if (this.captured.length - this.lastAnalyzedFrameCount >= LIVE_REANALYZE_EVERY_N_FRAMES) {
        this.lastAnalyzedFrameCount = this.captured.length;
        this.detectLiveImpacts();
        this.evaluateRallies();
      }

      this.emitFrame();
    } finally {
      this.inflight = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Frame helpers
  // ---------------------------------------------------------------------------

  private drawOverlay(
    canvas: HTMLCanvasElement | null,
    video: HTMLVideoElement,
    poses: readonly PoseFrame[],
  ): void {
    if (!canvas) return;
    const flags: OverlayFlags = this.settings.overlays;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    clearCanvas(ctx);
    if (flags.stickman) {
      for (const p of poses) drawStickman(ctx, p);
    }
    if (flags.courtPolygon && this.calibCorners && this.calibCorners.length === 4) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(this.calibCorners[0]![0], this.calibCorners[0]![1]);
      for (let i = 1; i < 4; i++)
        ctx.lineTo(this.calibCorners[i]![0], this.calibCorners[i]![1]);
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
  }

  private poseToCourtXY(frame: PoseFrame): { x: number; y: number } | null {
    const lh = frame[pose.LM.LEFT_HIP];
    const rh = frame[pose.LM.RIGHT_HIP];
    if (!lh || !rh) return null;
    const vis = Math.min(lh.visibility, rh.visibility);
    if (vis < 0.3) return null;
    const nx = (lh.x + rh.x) / 2;
    const ny = (lh.y + rh.y) / 2;
    const w = this.io.video.videoWidth ?? 0;
    const h = this.io.video.videoHeight ?? 0;
    return this.pixelToSinglesCourt(nx * w, ny * h, w, h);
  }

  private ballToCourtXY(n: { x: number; y: number }): { x: number; y: number } {
    const w = this.io.video.videoWidth ?? 0;
    const h = this.io.video.videoHeight ?? 0;
    return this.pixelToSinglesCourt(n.x * w, n.y * h, w, h);
  }

  private pixelToSinglesCourt(
    pxX: number,
    pxY: number,
    videoW: number,
    videoH: number,
  ): { x: number; y: number } {
    const H: Matrix3x3 | null = this.courtCtrl.state.homography;
    if (H) {
      const m = pixelToCourt([pxX, pxY], H);
      if (Number.isFinite(m.x) && Number.isFinite(m.y)) {
        const alleyM = (COURT_M.DOUBLES_WIDTH - COURT_M.SINGLES_WIDTH) / 2;
        const xSinglesM = m.x - alleyM;
        const propX = (xSinglesM / COURT_M.SINGLES_WIDTH) * COURT.W;
        const propY = (m.y / COURT_M.LENGTH) * COURT.L;
        return {
          x: clamp(propX, 0, COURT.W),
          y: clamp(propY, 0, COURT.L),
        };
      }
    }
    const nx = videoW > 0 ? pxX / videoW : pxX;
    const ny = videoH > 0 ? pxY / videoH : pxY;
    return { x: clamp(nx * COURT.W, 0, COURT.W), y: clamp(ny * COURT.L, 0, COURT.L) };
  }

  private detectLiveImpacts(): void {
    const video = this.io.video;
    const fps = video.duration > 0 ? this.captured.length / video.currentTime : 30;
    if (fps <= 0 || this.captured.length < 20) return;
    const { contacts } = analyzeSession(this.captured, fps, this.settings.handedness);
    this.lastKnownContacts = contacts;
    const newOnes: CourtImpact[] = [];
    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i]!;
      if (this.seenContactFrames.has(c.frameIndex)) continue;
      this.seenContactFrames.add(c.frameIndex);
      const speedKmh = Math.round(60 + c.peakAngularVelocity * 6);
      const impact = deterministicImpact(
        this.sessionSeed + i * 101,
        1,
        'Forehand',
        speedKmh,
      );
      newOnes.push(impact);
    }
    if (newOnes.length > 0) {
      this.liveImpacts = [...this.liveImpacts, ...newOnes];
      this.lastEvent = newOnes[newOnes.length - 1] ?? this.lastEvent;
    }
  }

  /**
   * After every re-analysis, check whether the contact list forms a
   * freshly-closed rally. When the last contact is older than `endGapMs`,
   * the rally is considered closed and `onRallyEnd` fires exactly once.
   */
  private evaluateRallies(): void {
    const contacts = this.lastKnownContacts;
    if (contacts.length === 0) {
      this.openRallyStartIdx = null;
      return;
    }
    const rallies = score.detectRallies(contacts);
    if (rallies.length === 0) return;
    const last = rallies[rallies.length - 1]!;

    // Determine whether the last rally is still "open" — i.e., the last
    // contact in it is recent relative to the playing clock. For live feeds
    // we cannot rely on video.duration, so use the most-recent capture time.
    const video = this.io.video;
    const nowMs =
      video.currentTime > 0
        ? video.currentTime * 1000
        : performance.now() - (contacts[0]?.timeMs ?? 0);
    const isClosed = nowMs - last.endTimeMs > 3500;

    if (isClosed) {
      // Every completed rally that we haven't yet announced — fire onRallyEnd.
      if (this.openRallyStartIdx !== last.startContactIdx) {
        this.opts.onRallyEnd?.(last);
        this.openRallyStartIdx = last.startContactIdx;
      }
    } else {
      // Still-open: announce only prior rallies we haven't seen yet.
      for (let i = 0; i < rallies.length - 1; i++) {
        const r = rallies[i]!;
        if (r.startContactIdx === this.openRallyStartIdx) continue;
        this.opts.onRallyEnd?.(r);
        this.openRallyStartIdx = r.startContactIdx;
      }
    }
  }

  private emitFrame(): void {
    this.opts.onFrame?.(this.state);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

async function safeCreateImageBitmap(video: HTMLVideoElement): Promise<ImageBitmap | null> {
  try {
    return await createImageBitmap(video);
  } catch {
    return null;
  }
}

function closeBitmap(b: ImageBitmap): void {
  try {
    b.close();
  } catch {
    // ImageBitmap.close() can throw in jsdom; swallow.
  }
}
